/**
 * TeamWorkplaceTension — Spanning tab
 *
 * Premium herzien volgens personakaart-DNA:
 * - Één anker-zin als hero (Groeier en Teamspeler zijn de kern...)
 * - Impact-uitspraken direct daarna als body
 * - Eén lijst voor "verder in het team" met witte kaarten + linker accent
 * - Subtiele scheidslijn tussen middenmoot en minderheid
 * - Check in je kantoor als afsluiter
 */

import React from 'react';
import { SPACING, TYPE, RADIUS, useIsMobile } from '../../ui/tokens';
import { useCopy } from '../../i18n/LanguageContext';

const PERSONA_COLORS = {
    maker: '#B05252',
    groeier: '#C28D6B',
    presteerder: '#C7A24A',
    denker: '#6F7F92',
    verbinder: '#7F9A8A',
    teamspeler: '#8B7F9A',
    zekerzoeker: '#7D8A6B',
    vernieuwer: '#D08C5B',
};

function colorForPersona(id) {
    return PERSONA_COLORS[id] || 'var(--tof-text)';
}

export default function TeamWorkplaceTension({ insights }) {
    const isMobile = useIsMobile();
    const t = useCopy().teamPanels.workplaceTension;
    const tension = insights?.workplaceTension || { impactSummary: null };
    const { impactSummary } = tension;

    if (!impactSummary) {
        return (
            <div style={{ padding: SPACING.lg, fontSize: 13, color: 'var(--tof-text-muted)' }}>
                {t.empty}
            </div>
        );
    }

    const { dominant, middle, minority, checkWorkplaces, uncheckWorkplaces } = impactSummary;

    // Combineer middle + minority tot één lijst, met groep-flag
    const restOfTeam = [
        ...middle.map((p) => ({ ...p, isMinority: false })),
        ...minority.map((p) => ({ ...p, isMinority: true })),
    ];

    return (
        <div
            style={{
                display: 'grid',
                gap: isMobile ? SPACING.lg : SPACING['2xl'],
            }}
        >
            {/* HERO: anker-zinnen + impact-uitspraken */}
            {dominant.length > 0 ? (
                <DominantHero personas={dominant} isMobile={isMobile} />
            ) : null}

            {/* Verder in het team — één lijst */}
            {restOfTeam.length > 0 ? (
                <RestOfTeam items={restOfTeam} isMobile={isMobile} />
            ) : null}

            {/* Check in je kantoor */}
            {(checkWorkplaces.length > 0 || uncheckWorkplaces.length > 0) ? (
                <CheckYourOffice
                    checkWorkplaces={checkWorkplaces}
                    uncheckWorkplaces={uncheckWorkplaces}
                    isMobile={isMobile}
                />
            ) : null}
        </div>
    );
}

// =========================
// DOMINANT HERO — ankerzin + impact
// =========================

function DominantHero({ personas, isMobile }) {
    const { teamPanels } = useCopy();
    const t = teamPanels.workplaceTension;
    const isOne = personas.length === 1;

    const personaNames = personas.map((p) => p.name);
    const personaNamesText = teamPanels.names.join(personaNames);

    // Som van team-percentages voor eyebrow-context
    const totalPct = personas.reduce((sum, p) => sum + (p.teamPct || 0), 0);
    const pctText = isOne
        ? t.shareOne(personas[0].teamPct)
        : personas.length === 2
            ? t.shareTwo(personas[0].teamPct, personas[1].teamPct)
            : t.shareMany(totalPct);

    const resultLine = t.impactResult(isOne);
    const energyLine = t.impactEnergy(isOne);

    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.sm,
                paddingBottom: isMobile ? SPACING.md : SPACING.lg,
                borderBottom: '1px solid var(--tof-border)',
            }}
        >
            {/* Eyebrow + percentage — zoals Werkplek hero */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: SPACING.md,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                    {t.coreEyebrow}
                </div>
                <div
                    style={{
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 13,
                        color: 'var(--tof-text-muted)',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {pctText}
                </div>
            </div>

            {/* GROOT statement in sage Playfair — zoals "Samenwerkplekken" bij Werkplek */}
            <h3
                style={{
                    margin: 0,
                    fontFamily: 'var(--tof-font-heading)',
                    fontSize: isMobile ? 32 : 'clamp(32px, 3.6vw, 46px)',
                    lineHeight: 1.0,
                    fontWeight: 500,
                    color: 'var(--tof-accent-sage-text)',
                    letterSpacing: '-0.01em',
                }}
            >
                {personaNamesText}
            </h3>

            {/* Impact-uitspraken */}
            <div style={{ display: 'grid', gap: SPACING.sm, maxWidth: 720, marginTop: SPACING.sm }}>
                <p
                    style={{
                        margin: 0,
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 14,
                        lineHeight: 1.65,
                        color: 'var(--tof-text-soft)',
                    }}
                >
                    {resultLine.before}
                    <span style={{ color: 'var(--tof-text)', fontWeight: 500 }}>
                        {resultLine.highlight}
                    </span>
                    {resultLine.after}
                </p>
                <p
                    style={{
                        margin: 0,
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 14,
                        lineHeight: 1.65,
                        color: 'var(--tof-text-soft)',
                    }}
                >
                    {energyLine.before}
                    <span style={{ color: 'var(--tof-text)', fontWeight: 500 }}>
                        {energyLine.highlight}
                    </span>
                    {energyLine.after}
                </p>
            </div>
        </div>
    );
}

// =========================
// REST OF TEAM — één lijst, witte kaarten met linker accent
// =========================

function RestOfTeam({ items, isMobile }) {
    const t = useCopy().teamPanels.workplaceTension;

    return (
        <div style={{ display: 'grid', gap: SPACING.md }}>
            <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                {t.restOfTeam}
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                    gap: SPACING.sm,
                }}
            >
                {items.map((persona) => (
                    <PersonaCard key={persona.id} persona={persona} />
                ))}
            </div>
        </div>
    );
}

function PersonaCard({ persona }) {
    const { teamPanels } = useCopy();
    const color = colorForPersona(persona.id);

    return (
        <div
            style={{
                background: 'var(--tof-surface)',
                borderLeft: `3px solid ${color}`,
                borderRadius: RADIUS.md,
                padding: '12px 14px',
                display: 'grid',
                gap: 6,
            }}
        >
            {/* Header: naam + percentage + minderheid-label */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: SPACING.sm,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.sm }}>
                    <h4
                        style={{
                            margin: 0,
                            fontFamily: 'var(--tof-font-heading)',
                            fontSize: 18,
                            lineHeight: 1.1,
                            fontWeight: 500,
                            color: 'var(--tof-text)',
                        }}
                    >
                        {persona.name}
                    </h4>
                    <span
                        style={{
                            fontFamily: 'var(--tof-font-body)',
                            fontSize: 12,
                            color: 'var(--tof-text-muted)',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {persona.teamPct}%
                    </span>
                </div>

                {persona.isMinority ? (
                    <span
                        style={{
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: 1.2,
                            fontWeight: 700,
                            color: 'var(--tof-text-muted)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {teamPanels.workplaceTension.isolationRisk}
                    </span>
                ) : null}
            </div>

            <p
                style={{
                    margin: 0,
                    fontFamily: 'var(--tof-font-body)',
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: 'var(--tof-text-soft)',
                }}
            >
                {persona.tensionMessage}
            </p>
        </div>
    );
}

// =========================
// CHECK IN JE KANTOOR
// =========================

function CheckYourOffice({ checkWorkplaces, uncheckWorkplaces, isMobile }) {
    const { workplaceTension } = useCopy().teamPanels;
    const t = workplaceTension.office;

    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.md,
                paddingTop: SPACING.md,
                borderTop: '1px solid var(--tof-border)',
            }}
        >
            <div style={{ display: 'grid', gap: SPACING.xs }}>
                <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                    {t.eyebrow}
                </div>
                {/* De kantoorvraag is de vraag waar dit blok antwoord op zoekt;
                    hij stond los, rechts uitgelijnd, bovenaan het paneel (A5.3). */}
                <p style={{ ...TYPE.body, fontSize: 14, margin: 0 }}>
                    {workplaceTension.headerQuestion}
                </p>
            </div>

            {/* Eén vraag per plek, op een eigen regel, zoals in de PDF
                (besluit 7, 27 okt); de pillen vervallen. */}
            {(checkWorkplaces.length > 0 || uncheckWorkplaces.length > 0) ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: SPACING.sm }}>
                    {checkWorkplaces.map((wp) => (
                        <li key={`enough-${wp.key}`} style={{ ...TYPE.body, fontSize: 14, margin: 0, color: 'var(--tof-text)' }}>
                            {t.enough(wp.label.toLowerCase())}
                        </li>
                    ))}
                    {uncheckWorkplaces.map((wp) => (
                        <li key={`used-${wp.key}`} style={{ ...TYPE.body, fontSize: 14, margin: 0, color: 'var(--tof-text)' }}>
                            {t.notFull(wp.label.toLowerCase())}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
